import axios from "axios";
import type { TaskListItem, TaskDetailResponse, TaskListPageResponse } from "./requests";
import { readPaginationHeaders } from "../utils/apiPagination";

export interface ItCheck {
  id?: string;
  url: string;
  productName: string;
  status?: string;
  schedule?: import("./requests").TaskSchedule[];
  available?: boolean;
  lastPing?: string | null;
  createdAt?: string;
}

export interface ItCheckSearch {
  queryString?: string;
  page?: number;
  pageSize?: number;
  direction?: "asc" | "desc";
}

export interface ItCheckRunResult {
  status?: string;
}

export const createItCheck = (data: Pick<ItCheck, "url" | "productName">): Promise<ItCheck> =>
  axios.post("/check/it", data).then((resp) => resp.data);

export const fetchItCheckList = (params: ItCheckSearch = {}): Promise<ItCheck[]> =>
  axios.post("/check/it/list", params).then((resp) => (Array.isArray(resp.data) ? resp.data : []));

export const fetchItCheckById = (id: string): Promise<ItCheck> =>
  axios.get(`/check/it/${encodeURIComponent(id)}`).then((resp) => resp.data);

export const editItCheck = (
  id: string,
  data: Pick<ItCheck, "url" | "productName">
): Promise<ItCheck> =>
  axios.put(`/check/it/${encodeURIComponent(id)}`, data).then((resp) => resp.data);

export const runItCheck = (id: string): Promise<ItCheckRunResult> =>
  axios
    .post(`/check/it/${encodeURIComponent(id)}/schedule`)
    .then((resp) => (resp.data ?? {}) as ItCheckRunResult);

export const fetchItCheckTaskListPage = (
  id: string,
  page: number,
  pageSize: number
): Promise<TaskListPageResponse> => {
  const apiPage = page;
  return axios
    .get(`/check/it/${encodeURIComponent(id)}/task`, {
      params: { page: apiPage, pageSize },
    })
    .then((resp) => {
      const items: TaskListItem[] = Array.isArray(resp.data) ? resp.data : [];
      const pagination = readPaginationHeaders(resp.headers ?? {}, {
        page: apiPage,
        pageSize,
        itemsLength: items.length,
      });
      return {
        items,
        page: pagination.page,
        pageSize: pagination.pageSize,
        totalPages: pagination.totalPages,
        totalElements: pagination.totalElements,
      };
    });
};

export const fetchItCheckTaskDetail = (
  id: string,
  taskId: string
): Promise<TaskDetailResponse> =>
  axios
    .get(`/check/it/${encodeURIComponent(id)}/task/${encodeURIComponent(taskId)}`)
    .then((resp) => resp.data);

export const toRequestLike = (item: ItCheck) => ({
  id: item.id ?? "",
  companyName: item.productName,
  inn: "",
  url: item.url,
  status: item.status ?? "",
  schedule: item.schedule,
  available: item.available,
  lastPing: item.lastPing,
});
